import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { getInspectionInstanceFilterAttributesQuery } from 'graphql-queries';

export interface InspectionInstanceFilterCompleter {
  id: string;
  name: string;
}

interface FilterAttributesData {
  inspectionInstanceFilterAttributes: {
    completers: Array<{
      completedById: string;
      completedBy?: {
        _id: string;
        name: string;
      } | null;
    }>;
    templateNames: string[];
  };
}

export interface UseGetInspectionInstanceFilterAttributesParams {
  channelId: string;
}

export interface UseGetInspectionInstanceFilterAttributesResult {
  completers: InspectionInstanceFilterCompleter[];
  templateNames: string[];
  loading: boolean;
  error: ReturnType<typeof useQuery>['error'];
}

export function useGetInspectionInstanceFilterAttributes(
  params: UseGetInspectionInstanceFilterAttributesParams
): UseGetInspectionInstanceFilterAttributesResult {
  const { data, loading, error } = useQuery<FilterAttributesData>(
    getInspectionInstanceFilterAttributesQuery,
    {
      variables: {
        channelId: params.channelId,
      },
      fetchPolicy: 'network-only',
    }
  );

  const attributes = data?.inspectionInstanceFilterAttributes;

  const completers = useMemo<InspectionInstanceFilterCompleter[]>(
    () =>
      (attributes?.completers ?? []).flatMap(completer =>
        completer.completedBy
          ? [{ id: completer.completedBy._id, name: completer.completedBy.name }]
          : []
      ),
    [attributes?.completers]
  );

  const templateNames = useMemo<string[]>(
    () => attributes?.templateNames ?? [],
    [attributes?.templateNames]
  );

  return {
    completers,
    templateNames,
    loading,
    error,
  };
}
